import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import i18n from './i18n';
import URLs from "./pages/urls";
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './components/AuthContext';
import { BasketProvider } from './components/BasketContext';
import { MenuProvider } from './components/MenuContext';
import MerchPlayground from './pages/core/home/MerchPlayground';
import './assets/fonts.css';

const IDLE_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

const App = () => {
  const [isLanguageLoaded, setIsLanguageLoaded] = useState(false);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    const storedLanguage = localStorage.getItem('language');
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage).then(() => {
        setIsLanguageLoaded(true);
      });
    } else {
      setIsLanguageLoaded(true);
    }
  }, []);

  // Idle timeout: track user activity
  useEffect(() => {
    const updateLastActivity = () => {
      lastActivityRef.current = Date.now();
    };
    
    window.addEventListener('mousemove', updateLastActivity);
    window.addEventListener('click', updateLastActivity);
    window.addEventListener('keypress', updateLastActivity);
    window.addEventListener('scroll', updateLastActivity);
    window.addEventListener('touchstart', updateLastActivity);

    return () => {
      window.removeEventListener('mousemove', updateLastActivity);
      window.removeEventListener('click', updateLastActivity);
      window.removeEventListener('keypress', updateLastActivity);
      window.removeEventListener('scroll', updateLastActivity);
      window.removeEventListener('touchstart', updateLastActivity);
    };
  }, []);

  // Check idle timeout every 60 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= IDLE_TIMEOUT) {
        window.location.reload();
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  if (!isLanguageLoaded) {
    return <div></div>;
  }

  return (
      <ErrorBoundary>
        <HelmetProvider>
          <I18nextProvider i18n={i18n}>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AuthProvider>
                <BasketProvider>
                  <MenuProvider>
                    <div className="site-scale-root min-h-screen overflow-x-hidden">
                      <URLs />
                    </div>
                    <MerchPlayground globalOnly />
                  </MenuProvider>
                </BasketProvider>
              </AuthProvider>
            </BrowserRouter>
          </I18nextProvider>
        </HelmetProvider>
      </ErrorBoundary>
  );
};

export default App;
